import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'components/Button';
import ItemDetails from 'components/ItemDetails';
import ContactAgentModal from 'components/modal/ContactAgentModal';
import { setSelectedQuote, setCanvasQuoteDocuments } from 'reducers/ui/uiSlice';
import { BUTTON_TYPE_LINK } from 'shared/constants/common';
import { ROUTES_SELF_SERVICE } from 'shared/constants/selfServiceRoutes';
import { getCanvasQuoteDocuments } from 'services/CommonService';
import { displayAmount, displayDate } from 'shared/helpers/common';
import {
  getQuoteBookingComponentOverview,
  getPassengersToDisplay,
  generateTitle,
} from 'shared/helpers/quoteBooking';

export default function QuotesItem({ quote = {} }) {
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useState(false);
  const { componentConfig, canvasQuoteDocuments } = useSelector((state) => state.ui);

  const {
    id,
    reference,
    currency,
    currentPrice,
    price,
    passengerSummary,
    author = {},
    outbound,
    destination,
    destinations,
    accommodation,
    cruises,
    tours,
    flights,
    due,
    balanceDueDate,
  } = quote;

  useEffect(() => {
    const fetchData = async () => {
      if (componentConfig.showQuoteDocuments && Object.keys(canvasQuoteDocuments).length === 0) {
        try {
          const { data: canvasQuotesData } = await getCanvasQuoteDocuments();
          dispatch(setCanvasQuoteDocuments(canvasQuotesData || {}));
        } catch (error) {
          console.error('Error fetching canvasQuoteDocuments', error);
        }
      }
    };

    fetchData();
  }, [componentConfig.showQuoteDocuments, canvasQuoteDocuments, dispatch]);

  const isCanvasQuote = canvasQuoteDocuments && reference && canvasQuoteDocuments[reference];
  const viewQuoteUrl = isCanvasQuote
    ? canvasQuoteDocuments[reference]
    : `${ROUTES_SELF_SERVICE.QUOTES}/${id || reference}`;

  const {
    name: componentName,
    dateToDisplay,
    componentType,
  } = getQuoteBookingComponentOverview(quote);

  const passengerDisplay = getPassengersToDisplay(passengerSummary);

  const selectQuote = () => {
    dispatch(setSelectedQuote(quote));
  };

  const openModalHandler = () => {
    setOpenModal(!openModal);
  };

  return (
    <>
      <div className="rounded-[10px] mt-[40px] bg-white flex flex-col lg:flex-row lg:justify-between">
        <ItemDetails
          className="p-4 w-full"
          title={generateTitle(
            outbound,
            destination,
            destinations,
            accommodation,
            cruises,
            tours,
            flights,
          )}
          componentType={componentType}
          componentName={componentName}
          date={dateToDisplay}
          guest={passengerDisplay}
          totalPrice={displayAmount(currentPrice || price, currency)}
          due={displayAmount(due, currency)}
          balanceDueDate={displayDate(balanceDueDate, '-', undefined, true)}
          reference={reference}
          isHorizontal
          type="quote"
        />
        <div
          className={`flex flex-col border-t lg:border-l lg:border-t-0 lg:w-[300px]
          ${
            componentConfig.contactAgentEnabled
              ? 'lg:justify-center'
              : 'lg:justify-end'
          }`}
        >
          {componentConfig.contactAgentEnabled && (
            <Button
              label="Message Customer Services"
              secondary
              className="mt-3 mx-3 !text-[13px]"
              onClick={openModalHandler}
            />
          )}
          <Button
            label="View quote"
            className="m-3"
            type={BUTTON_TYPE_LINK}
            onClick={selectQuote}
            url={viewQuoteUrl}
            isNewTab={!!isCanvasQuote}
          />
        </div>
      </div>
      <ContactAgentModal
        assignee={author}
        reference={reference}
        openModal={openModal}
        setOpenModal={setOpenModal}
        showReference
        context="quote"
      />
    </>
  );
}
